import React, { useState } from "react";
import { Icon } from "../UIElements/Icon";
import { Button } from "../UIElements/Button";
import { useGlobalUtils } from "../../../lib/globalUtils";
import WalletTransactionModal from "./WalletTransactionModal";
import WalletTransactionHistoryModal from "./WalletTransactionHistoryModal";

const WalletCard = ({ wallet, userId, users = [] }) => {
  const { parsePrice, parseDate } = useGlobalUtils();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeductModal, setShowDeductModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const getWalletDisplayName = (type) => {
    switch (type) {
      case "cash":
        return "Cash Wallet";
      case "credit_point":
        return "Credit Points";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const getWalletIcon = (type) => {
    switch (type) {
      case "cash":
        return "DollarSign";
      case "credit_point":
        return "Star";
      default:
        return "Wallet";
    }
  };

  const getWalletColor = (type) => {
    switch (type) {
      case "cash":
        return "text-success";
      case "credit_point":
        return "text-warning";
      default:
        return "text-primary";
    }
  };

  return (
    <div className="border-border bg-card rounded-lg border p-6">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-muted rounded-lg p-3">
            <Icon
              name={getWalletIcon(wallet.type)}
              className={`h-6 w-6 ${getWalletColor(wallet.type)}`}
            />
          </div>
          <div>
            <h4 className="text-foreground text-lg font-semibold">
              {getWalletDisplayName(wallet.type)}
            </h4>
            <p className="text-muted-foreground text-sm">
              {wallet.type === "cash" ? "Cash Balance" : "Credit Points"}
            </p>
          </div>
        </div>
        <div
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            wallet.locked
              ? "border-error/40 bg-error-bg text-error border"
              : "border-success/40 bg-success-bg text-success border"
          }`}
        >
          {wallet.locked ? "Locked" : "Active"}
        </div>
      </div>

      <div className="space-y-4">
        {/* Balance */}
        <div className="text-center">
          <p className="text-muted-foreground mb-1 text-sm">Current Balance</p>
          <p className={`text-3xl font-bold ${getWalletColor(wallet.type)}`}>
            {wallet.type === "credit_point"
              ? `${wallet.balance} pts`
              : parsePrice(wallet.balance)}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {/* History Button */}
          <Button
            variant="ghost"
            onClick={() => setShowHistoryModal(true)}
            className="bg-primary text-primary-foreground hover:bg-primary flex flex-1 items-center justify-center space-x-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
          >
            <Icon name="History" className="h-4 w-4" />
            <span>History</span>
          </Button>

          {/* Add/Deduct Buttons */}
          {!wallet.locked && (
            <>
              <Button
                variant="ghost"
                onClick={() => setShowAddModal(true)}
                className="bg-success hover:bg-success/90 flex flex-1 items-center justify-center space-x-2 rounded-lg px-3 py-2 text-sm font-medium text-[var(--success-foreground)] transition-colors"
              >
                <Icon name="Plus" className="h-4 w-4" />
                <span>Add</span>
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowDeductModal(true)}
                className="bg-error hover:bg-error/90 flex flex-1 items-center justify-center space-x-2 rounded-lg px-3 py-2 text-sm font-medium text-[var(--error-foreground)] transition-colors"
              >
                <Icon name="Minus" className="h-4 w-4" />
                <span>Deduct</span>
              </Button>
            </>
          )}
        </div>

        {/* Wallet Details */}
        <div className="space-y-3">
          {wallet.locked && wallet.locked_reason && (
            <div className="border-error/40 bg-error-bg mt-3 rounded-lg border p-3">
              <p className="text-error text-sm">
                <span className="font-medium">Lock Reason:</span>{" "}
                {wallet.locked_reason}
              </p>
              {wallet.locked_at && (
                <p className="text-error mt-1 text-xs">
                  Locked on: {parseDate(wallet.locked_at)}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">Created:</span>
            <span className="text-foreground text-sm">
              {parseDate(wallet.created_at)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">Last Updated:</span>
            <span className="text-foreground text-sm">
              {parseDate(wallet.updated_at)}
            </span>
          </div>
        </div>
      </div>

      {/* Transaction Modals */}
      <WalletTransactionModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        wallet={wallet}
        userId={userId}
        transactionType="add"
      />

      <WalletTransactionModal
        isOpen={showDeductModal}
        onClose={() => setShowDeductModal(false)}
        wallet={wallet}
        userId={userId}
        transactionType="deduct"
      />

      <WalletTransactionHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        wallet={wallet}
        userId={userId}
        users={users}
      />
    </div>
  );
};

export default WalletCard;
